import { ApolloClient, createHttpLink, DocumentNode, gql, InMemoryCache, TypedDocumentNode } from '@apollo/client';
import { cloneDeep, isEmpty, set } from 'lodash';
import {
    ContactInput,
    LinkedPeopleInfo,
    LinkedPeopleInfoInput,
    MoneyInput,
    Query,
    QueryProductionArgs,
    Resource
} from 'sr-types/lib/production/v1/graphql';
import { SCHEDULE_FIELDS } from '../../admin/templates/resources/resources';
import { authLink } from '../../common/auth/api';
import { Location, LOCATION_FIELDS } from '../../common/components/location';
import { Summary } from '../../common/components/types';
import { Icons } from '../../common/icons/Icons';
import {
    ContactFragment,
    DateRangeFragment,
    FieldFragment,
    ReferenceFragment,
    ShareAccessFragment
} from '../../common/list/fragments';
import { Reference, ReferenceField } from '../../common/reference/reference';
import {
    createEmptyAboutInfo,
    EMPTY_ALT_IDS,
    EMPTY_ASSOCIATED_ORGS,
    EMPTY_ASSOCIATED_PPL,
    EMPTY_PROD_INFO,
    EMPTY_PROJECT_TRACK
} from '../form/ProductionEditor';
import { EMPTY_PRODUCTION_DATES } from './productionUtils';

export const client = new ApolloClient({
    link: authLink.concat(
        createHttpLink({
            uri: '/production/v1/'
        })
    ),
    cache: new InMemoryCache({
        addTypename: false
    })
});

export const bulkSaveQuery: DocumentNode = gql`
    mutation BulkSaveProduction($input: BulkSaveProductionRequest!) {
        bulkSaveProduction(input: $input) {
            createdCount
            updatedCount
            unprocessedCount
            results {
                id
                errors {
                    field
                    message
                }
                reference {
                    id
                }
            }
            logFile {
                id
                uriPrefix
            }
        }
    }
`;

export const bulkSaveProductionActivityQuery: DocumentNode = gql`
    mutation BulkSaveActivity($productionId: String!, $planId: String!, $input: BulkSaveActivityRequest!) {
        bulkSaveActivity(productionId: $productionId, planId: $planId, input: $input) {
            createdCount
            updatedCount
            unprocessedCount
            results {
                id
                errors {
                    field
                    message
                }
                reference {
                    id
                }
            }
            logFile {
                id
                uriPrefix
            }
        }
    }
`;

export const saveQuery: DocumentNode = gql`
    mutation SaveProduction($input: ProductionInput!) {
        saveProduction(input: $input) {
            id
            errors {
                field
                message
            }
            reference {
                id
                type
                label
                deleted
                eventId
            }
        }
    }
`;

export const preSaveQuery: DocumentNode = gql`
    mutation PreSaveProduction {
        preSaveProduction {
            id
            errors {
                field
                message
            }
        }
    }
`;

export const detailsQuery: DocumentNode = gql`
    query Production($id: ID!) {
        production(id: $id) {
            identity {
                id
            }
            about
            budget {
                currencyCode
                nanos
                units
            }
            name
            tags
            otherTitles {
                key
                value
            }
            projectOwners {
                ...Reference
            }
            createdFrom {
                ...Reference
            }
            projectStatus
            productionType
            productionSubType
            productionStatus
            genre
            releaseDate
            releaseYear
            productionYears
            storageAssets {
                referenceName
                provider
                storageUrl {
                    path
                    tldomain
                    subdomain
                }
            }
            showAttributes {
                key
                value
            }
            productionGroup {
                label
                productionReference {
                    id
                    eventId
                    uriPrefix
                    type
                    label
                    deleted
                }
            }
            linkedPeople {
                id
                contact {
                    id
                    name {
                        firstName
                        lastName
                        salutation
                        suffix
                    }
                    isPrimary
                    contactInfo {
                        email {
                            address
                            typeString
                            verified
                        }
                        phone {
                            number
                            typeString
                        }
                    }
                    personReference {
                        ...Reference
                    }
                    role
                    roles
                    organization
                    organizationReference {
                        ...Reference
                    }
                    label
                    production
                    productionRole
                    productionRoles
                    productionReference {
                        ...Reference
                    }
                    title
                    organizationDepartment
                    productionDepartment
                }
                hiringStatus
                resourceLabel
                resourceSubType
                resourceType
            }
            alternateIds {
                label
                value
                url
            }
            locations {
                ...LocationFields
            }
            associatedOrgs {
                name
                organizationReference {
                    ...Reference
                }
                organizationTypes
                organizationCategories {
                    value
                    lineage {
                        value
                    }
                }
                productionRoles
            }
            managedProduction
            productionDates {
                autoSetDate
                dateRange {
                    ...DateRange
                }
            }
            keyActivities {
                name
                activityType
                durationSpecified
                dateRange {
                    ...DateRange
                }
                locations {
                    label
                    primary
                }
            }
            plans {
                name
                uri
                description
                createdOn
                updatedOn
                identity {
                    id
                    preSave
                }
                keyActivities {
                    id
                    eventId
                    uriPrefix
                    type
                    label
                    deleted
                }
                activities(page: { size: 1000, from: 0 }) {
                    identity {
                        id
                    }
                    name
                    uri
                    activityType
                    tags
                    durationSpecified
                    createdOn
                    updatedOn
                    category {
                        value
                        lineage {
                            value
                        }
                    }
                    dateRange {
                        ...DateRange
                    }
                    summary {
                        headline
                        about
                    }
                }
            }
            keyPeople {
                id
                name {
                    firstName
                    lastName
                    salutation
                    suffix
                }
                isPrimary
                contactInfo {
                    email {
                        address
                        typeString
                        verified
                    }
                    phone {
                        number
                        typeString
                    }
                }
                personReference {
                    ...Reference
                }
                role
                organization
                organizationReference {
                    ...Reference
                }
                label
                production
                productionRole
                title
                organizationDepartment
                productionDepartment
                roles
                productionRoles
            }
            keyOrganizations {
                name
                organizationReference {
                    ...Reference
                }
                organizationTypes
                organizationCategories {
                    lineage {
                        value
                    }
                    value
                }
                productionRoles
            }
            ownerOrganizationId
            ownerOrganizationName
            reference {
                ...Reference
            }
            security {
                allowedActions
            }
            currentStatusNoteReference {
                ...Reference
            }
            associatedFieldDefinitionReferences {
                ...Reference
            }
            attributes {
                ...Field
            }
            projectOwnerContacts {
                ...Contact
            }
        }
    }
    ${DateRangeFragment}
    ${LOCATION_FIELDS}
    ${FieldFragment}
    ${ReferenceFragment}
    ${ContactFragment}
`;

export const schedulerDetailsQuery: TypedDocumentNode<Pick<Query, 'production'>, QueryProductionArgs> = gql`
    query SchedulerProduction($id: ID!) {
        production(id: $id) {
            identity {
                id
            }
            about
            budget {
                currencyCode
                nanos
                units
            }
            name
            tags
            otherTitles {
                key
                value
            }
            projectOwners {
                ...Reference
            }
            createdFrom {
                ...Reference
            }
            projectStatus
            productionType
            productionSubType
            productionStatus
            genre
            releaseDate
            releaseYear
            productionYears
            storageAssets {
                referenceName
                provider
                storageUrl {
                    path
                    tldomain
                    subdomain
                }
            }
            showAttributes {
                key
                value
            }
            productionGroup {
                label
                productionReference {
                    id
                    eventId
                    uriPrefix
                    type
                    label
                    deleted
                }
            }
            linkedPeople {
                id
                contact {
                    id
                    name {
                        firstName
                        lastName
                        salutation
                        suffix
                    }
                    isPrimary
                    contactInfo {
                        email {
                            address
                            typeString
                            verified
                        }
                        phone {
                            number
                            typeString
                        }
                    }
                    personReference {
                        ...Reference
                    }
                    role
                    roles
                    organization
                    organizationReference {
                        ...Reference
                    }
                    label
                    production
                    productionRole
                    productionRoles
                    productionReference {
                        ...Reference
                    }
                    title
                    organizationDepartment
                    productionDepartment
                }
                hiringStatus
                resourceLabel
                resourceSubType
                resourceType
            }
            alternateIds {
                label
                value
                url
            }
            locations {
                ...LocationFields
            }
            associatedOrgs {
                name
                organizationReference {
                    ...Reference
                }
                organizationTypes
                organizationCategories {
                    value
                    lineage {
                        value
                    }
                }
                productionRoles
            }
            managedProduction
            productionDates {
                autoSetDate
                dateRange {
                    ...DateRange
                }
            }
            keyActivities {
                name
                activityType
                durationSpecified
                dateRange {
                    ...DateRange
                }
                locations {
                    label
                    primary
                }
            }
            keyPeople {
                id
                name {
                    firstName
                    lastName
                    salutation
                    suffix
                }
                isPrimary
                contactInfo {
                    email {
                        address
                        typeString
                        verified
                    }
                    phone {
                        number
                        typeString
                    }
                }
                personReference {
                    ...Reference
                }
                role
                organization
                organizationReference {
                    ...Reference
                }
                label
                production
                productionRole
                title
                organizationDepartment
                productionDepartment
                roles
                productionRoles
            }
            keyOrganizations {
                name
                organizationReference {
                    ...Reference
                }
                organizationTypes
                organizationCategories {
                    lineage {
                        value
                    }
                    value
                }
                productionRoles
            }
            ownerOrganizationId
            ownerOrganizationName
            reference {
                ...Reference
            }
            security {
                allowedActions
            }
            currentStatusNoteReference {
                ...Reference
            }
            associatedFieldDefinitionReferences {
                ...Reference
            }
            attributes {
                ...Field
            }
            projectOwnerContacts {
                ...Contact
            }
        }
    }
    ${DateRangeFragment}
    ${LOCATION_FIELDS}
    ${FieldFragment}
    ${ReferenceFragment}
    ${ContactFragment}
`;

export const deleteQuery: DocumentNode = gql`
    mutation DeleteProduction($id: ID!) {
        deleteProduction(id: $id) {
            id
            errors {
                field
                message
            }
        }
    }
`;

export const autoCreateQuery: DocumentNode = gql`
    mutation AutoCreateProduction($input: ProductionInput!) {
        autoCreateProduction(input: $input) {
            id
            reference {
                id
                eventId
                type
                uriPrefix
                deleted
            }
            errors {
                field
                message
            }
        }
    }
`;

export const saveActivity: DocumentNode = gql`
    mutation SaveActivity($productionId: String!, $planId: String!, $input: ActivityInput) {
        saveActivity(productionId: $productionId, planId: $planId, input: $input) {
            id
            errors {
                field
                key
                message
            }
        }
    }
`;

export const saveActivities: DocumentNode = gql`
    mutation SaveActivities($productionId: String!, $planId: String!, $input: [ActivityInput]) {
        saveActivities(productionId: $productionId, planId: $planId, input: $input) {
            id
            errors {
                field
                key
                message
            }
        }
    }
`;

export const activityDetails: DocumentNode = gql`
    query Activity($activityId: ID!) {
        activity(activityId: $activityId) {
            name
            uri
            activityType
            durationSpecified
            createdOn
            updatedOn
            activityStatus
            tags
            activityOwner {
                ...Reference
            }
            category {
                value
                lineage {
                    value
                }
            }
            identity {
                id
                preSave
            }
            dateRange {
                ...DateRange
            }
            locations {
                label
                primary
            }
            linkedResources {
                name
                type
                role
                status
                resourceCategory
                budgetCategory
                createdOn
                updatedOn
                identity {
                    id
                    preSave
                }
            }
            category {
                value
                lineage {
                    value
                }
            }
            summary {
                headline
                about
            }
            dependencies {
                lag
                lagUnit
                type
                from {
                    id
                    eventId
                    uriPrefix
                    type
                    label
                    deleted
                }
                to {
                    id
                    eventId
                    uriPrefix
                    type
                    label
                    deleted
                }
            }
        }
    }
    ${DateRangeFragment}
    ${ReferenceFragment}
`;

export const deleteActivity: DocumentNode = gql`
    mutation DeleteActivity($id: ID!) {
        deleteActivity(id: $id) {
            id
            errors {
                field
                key
                message
            }
        }
    }
`;

export const linkResourceToActivity: DocumentNode = gql`
    mutation LinkResourceToActivity($activityId: String!, $resourceId: String!) {
        linkResourceToActivity(activityId: $activityId, resourceId: $resourceId) {
            id
            errors {
                field
                key
                message
            }
        }
    }
`;

export const unlinkResourceFromActivity: DocumentNode = gql`
    mutation UnlinkResourceFromActivity($activityId: String!, $resourceId: String!) {
        unlinkResourceFromActivity(activityId: $activityId, resourceId: $resourceId) {
            id
            errors {
                field
                key
                message
            }
        }
    }
`;

export const createProductionFromTemplateMutation: DocumentNode = gql`
    mutation CreateProductionFromTemplate($templateId: ID!, $input: CreateProductionFromTemplateInput!) {
        createProductionFromTemplate(id: $templateId, input: $input) {
            id
            errors {
                field
                message
            }
        }
    }
`;

export const saveResourcesQuery: DocumentNode = gql`
    mutation SaveResources($productionId: String!, $input: [ResourceInput]!, $createShortlist: Boolean) {
        saveResources(productionId: $productionId, input: $input, createShortlist: $createShortlist) {
            id
            reference {
                id
                type
                deleted
                eventId
            }
            errors {
                field
                key
                message
            }
        }
    }
`;

export const saveResourceQuery: DocumentNode = gql`
    mutation SaveResource($productionId: String!, $input: ResourceInput!) {
        saveResource(productionId: $productionId, input: $input) {
            id
            reference {
                id
                type
                deleted
                eventId
            }
            errors {
                field
                message
            }
        }
    }
`;

export const resourceDetailsQuery: DocumentNode = gql`
    query Resource($resourceId: ID!) {
        resource(resourceId: $resourceId) {
            identity {
                id
            }
            name
            type
            role
            status
            resourceCategory
            budgetCategory
            summary {
                headline
                about
            }
            resourceSchedule {
                ...ScheduleFields
            }
            defaultNoteReference {
                id
                type
            }
            locations {
                ...LocationFields
            }
            linkedActivities {
                identity {
                    id
                }
                name
                activityType
                dateRange {
                    ...DateRange
                }
                locations {
                    ...LocationFields
                }
                security {
                    allowedActions
                }
            }
            resourceProfile {
                attributeName
                value
            }
            reference {
                ...Reference
            }
            associatedFieldDefinitionReferences {
                ...Reference
            }
            attributes {
                name
                fieldType
                value
                fieldDefinitionReference {
                    ...Reference
                }
            }
            hiringTeam {
                identity {
                    id
                    preSave
                }
                members {
                    ...Reference
                }
            }
            hiredOrganization {
                ...Reference
            }
            hiredPerson {
                ...Reference
            }
            acquisitionLead {
                ...Reference
            }
            subType
            state
            bookings {
                ...Reference
            }
            orders {
                ...Reference
            }
            proposals {
                ...Reference
            }
            security {
                allowedActions
            }
            createdBy
            createdByName
            restricted
            sharedWithAccounts {
                ...ShareAccess
            }
            sharedWithDepartments {
                ...ShareAccess
            }
        }
    }
    ${LOCATION_FIELDS}
    ${SCHEDULE_FIELDS}
    ${DateRangeFragment}
    ${ReferenceFragment}
    ${ShareAccessFragment}
`;

export const resourceDeleteQuery: DocumentNode = gql`
    mutation DeleteResource($id: ID!) {
        deleteResource(id: $id) {
            id
            errors {
                field
                message
            }
        }
    }
`;

export const addPersonToProduction: DocumentNode = gql`
    mutation AddPerson($productionId: ID!, $input: ManagedLinkedPeopleInput) {
        saveAssociatedPerson(productionId: $productionId, input: $input) {
            id
            errors {
                field
                message
            }
        }
    }
`;

export const addPeopleToProduction: DocumentNode = gql`
    mutation addAssociatedPeople($productionId: ID!, $input: [LinkedPeopleInfoInput]) {
        saveAssociatedPeople(productionId: $productionId, input: $input) {
            id
            errors {
                field
                message
            }
        }
    }
`;

export const addOrganizationToProduction: DocumentNode = gql`
    mutation addAssociatedOrganization($productionId: ID!, $input: ProductionOrganizationInfoInput!) {
        saveAssociatedOrganization(productionId: $productionId, input: $input) {
            id
            errors {
                field
                message
            }
        }
    }
`;

export const addPersonsToProduction: DocumentNode = gql`
    mutation SaveAssociatedPersons($productionId: ID!, $input: [ManagedLinkedPeopleInput]) {
        saveAssociatedPersons(productionId: $productionId, input: $input) {
            id
            errors {
                field
                key
                message
            }
        }
    }
`;

export const promoteSubmissionToWorkspace: DocumentNode = gql`
    mutation PipelineToWorkspace($pipelineId: ID!) {
        pipelineToWorkspace(pipelineId: $pipelineId) {
            id
            errors {
                field
                message
            }
            reference {
                ...Reference
            }
        }
    }
    ${ReferenceFragment}
`;

export const removePersonFromProduction: DocumentNode = gql`
    mutation RemovePerson($productionId: ID!, $input: LinkedPeopleInfoInput) {
        deleteAssociatedPerson(productionId: $productionId, input: $input) {
            id
            errors {
                field
                message
            }
        }
    }
`;

export const keyActivities: DocumentNode = gql`
    query KeyActivities($productionId: String!) {
        keyActivities(productionId: $productionId) {
            name
            uri
            activityType
            durationSpecified
            createdOn
            updatedOn
            identity {
                id
                preSave
            }
            dateRange {
                ...DateRange
            }
        }
    }
    ${DateRangeFragment}
`;

export const markKeyActivity: DocumentNode = gql`
    mutation MarkKeyActivity($activityId: String!) {
        markKeyActivity(activityId: $activityId) {
            id
            errors {
                field
                message
            }
        }
    }
`;

export const saveKeyActivities: DocumentNode = gql`
    mutation SaveKeyActivities($planId: ID!, $input: [ReferenceInput]) {
        saveKeyActivities(planId: $planId, input: $input) {
            id
            errors {
                field
                message
            }
        }
    }
`;

export const saveKeyPeople: DocumentNode = gql`
    mutation SaveKeyPeople($productionId: ID!, $input: [ContactInput]) {
        saveKeyPeople(productionId: $productionId, input: $input) {
            id
            errors {
                field
                message
            }
        }
    }
`;

export const savekeyOrganizations: DocumentNode = gql`
    mutation SaveKeyOrganizations($productionId: ID!, $input: [ProductionOrganizationInfoInput]) {
        saveKeyOrganizations(productionId: $productionId, input: $input) {
            id
            errors {
                field
                message
            }
        }
    }
`;

export const updateTaskMutation: DocumentNode = gql`
    mutation UpdateTaskAsDone($activityId: ID!, $isDone: Boolean!) {
        updateTaskAsDone(activityId: $activityId, isDone: $isDone) {
            id
            errors {
                field
                key
                message
            }
        }
    }
`;

export const saveStorageAssetMutation: DocumentNode = gql`
    mutation SaveStorageAsset($productionId: ID!, $input: ManageStorageAssetInput) {
        saveStorageAsset(productionId: $productionId, input: $input) {
            id
            errors {
                field
                key
                message
            }
        }
    }
`;

export const storageAssetsDetailsQuery: DocumentNode = gql`
    query StorageAssets($productionId: String!) {
        storageAssets(productionId: $productionId) {
            referenceName
            provider
            storageUrl {
                path
                tldomain
                subdomain
            }
        }
    }
`;

export const deleteStorageAssetMutation: DocumentNode = gql`
    mutation DeleteStorageAsset($productionId: ID!, $input: StorageAssetLinkInput) {
        deleteStorageAsset(productionId: $productionId, input: $input) {
            id
            errors {
                field
                key
                message
            }
            reference {
                ...Reference
            }
        }
    }
    ${ReferenceFragment}
`;

export const associateStatusNote: DocumentNode = gql`
    mutation AssociateCurrentStatusNote($productionId: ID!, $note: ReferenceInput!) {
        associateCurrentStatusNote(productionId: $productionId, note: $note) {
            id
            errors {
                field
                key
                message
            }
            reference {
                ...Reference
            }
        }
    }
    ${ReferenceFragment}
`;

export type ProductionInfo = {
    name: string;
    aka: string;
    productionType: string;
    productionSubType: string;
    productionStatus: string;
    productionGroup: ReferenceField;
    genre: Array<string>;
    managedProduction: boolean;
    keyPeople: any;
};

export type ProductionAboutInfo = {
    summary: {
        headline?: string;
        about?: string;
    };
    releaseDate: string;
    releaseYear: string;
    productionYears: string;
    showAttributes: Array<Record<any, any>>;
    locations: Array<Location>;
    budget: MoneyInput;
};

export type AssociatedOrganizations = {
    id?: string | number;
    organizationReference: undefined;
    organization: '';
    resourceCategory: '';
    categoryLineage: '';
    productionRoles: [];
};

export type ProductionBasicInfo = ProductionInfo & ProductionAboutInfo;

export const PRODUCT_TYPE_ICONS = {
    Animal: Icons.Animal,
    Cast: Icons.Cast,
    Crew: Icons.Crew,
    Equipment: Icons.Equipement,
    Place: Icons.Equipement,
    Product: Icons.Product,
    Corporate: Icons.Corporate,
    // Prop:
    Service: Icons.Services,
    Vehicle: Icons.Vehicle,
    Facility: Icons.Facility,
    // Wardrobe:
    Default: Icons.BuildCircleOutlined
};

export type ProductionResourceDetailsData = {
    resource?: Resource;
};

export type MarketPlaceBaseItem = {
    name: string | { firstName: string; lastName: string };
    publicName?: string;
    headline?: string;
    about?: string;
    summary?: Summary;
    organization?: string;
    experiences?: { organizationReference: Reference }[];
    label?: string;
    location?: Location | { label: string };
    locations?: Location[];
};

export type OperationPayload = {
    [key: string]: string;
};

export type MutationFunction = (Options: any) => Promise<any>;

export const toAssociatedOrgs = (orgs) => {
    return orgs.map((org) => {
        return {
            organizationReference: org.organizationReference,
            name: org.organization,
            organizationTypes: org.organizationTypes,
            organizationCategories: org.organizationCategories,
            productionRoles: org.productionRoles
        };
    });
};

export const toAssociatedPeople = (people: LinkedPeopleInfo[], production = undefined): LinkedPeopleInfoInput[] => {
    return people.map((p) => {
        const c = {
            name: p.contact?.name,
            contactInfo: p.contact?.contactInfo,
            personReference: p.contact?.personReference,
            role: isEmpty(p.contact?.roles) ? undefined : p.contact?.roles[0],
            roles: p.contact?.roles,
            organization: p.contact?.organization,
            organizationReference: p.contact?.organizationReference,
            productionDepartment: p.contact?.productionDepartment,
            organizationDepartment: p.contact?.organizationDepartment,
            title: p.contact?.title,
            productionRole: isEmpty(p.contact?.productionRoles) ? undefined : p.contact?.productionRoles[0],
            productionRoles: p.contact?.productionRoles
        } as ContactInput;
        if (production) {
            c.production = production.name;
            c.productionReference = {
                id: production.id
            };
        }
        const linkedPeopleInfoInput: LinkedPeopleInfoInput = {
            contact: c
        };
        if (p.resourceType) linkedPeopleInfoInput.resourceType = p.resourceType;
        if (p.resourceSubType) linkedPeopleInfoInput.resourceSubType = p.resourceSubType;
        if (p.resourceLabel) linkedPeopleInfoInput.resourceLabel = p.resourceLabel;
        return linkedPeopleInfoInput;
    });
};

export const rowsToProduction = (rows, colDefs) => {
    const alternateIdTypes = colDefs.map((col) => {
        if (col.field.startsWith('AltId')) {
            return col.headerName;
        }
    });
    return rows.map((row) => {
        const productionEntity = {
            publicTitle: row.publicTitle,
            summary: {
                headline: '',
                about: row.summary
            },
            productionStatus: row.productionStatus,
            productionType: row.productionType,
            productionGroup: {
                label: row.productionGroup
            },
            releaseDate: row.releaseDate,
            productionYears: row.productionYears,
            genre: isEmpty(row.genre) ? [] : row.genre?.split(';'),
            alternateIds: [],
            showAttributes: []
        };
        if (row.ID) set(productionEntity, 'identity', { id: row.ID });
        if (row.otherTitles)
            set(productionEntity, 'otherTitles', [
                {
                    key: 'AKA',
                    value: row.otherTitles
                }
            ]);
        if (row.runtime) productionEntity.showAttributes.push({ key: 'runtime', value: row.runtime });
        if (row.seasonNo) productionEntity.showAttributes.push({ key: 'seasonNo', value: row.seasonNo });
        if (row.episodeNo) productionEntity.showAttributes.push({ key: 'episodeNo', value: row.episodeNo });
        if (row.noOfEpisodes) productionEntity.showAttributes.push({ key: 'noOfEpisodes', value: row.noOfEpisodes });
        alternateIdTypes.forEach((type) => {
            if (row[`AltId${type}`]) {
                const id = row[`AltId${type}`].split(';');
                productionEntity.alternateIds.push({
                    label: type,
                    value: id[0],
                    url: id[1]
                });
            }
        });
        return productionEntity;
    });
};

export const createEmptyProduction = (locale: string) => {
    return {
        headline: '',
        otherTitles: [],
        ...cloneDeep(EMPTY_PROD_INFO),
        ...cloneDeep(createEmptyAboutInfo(locale)), // now locale-aware
        ...cloneDeep(EMPTY_ALT_IDS),
        ...cloneDeep(EMPTY_ASSOCIATED_PPL),
        ...cloneDeep(EMPTY_ASSOCIATED_ORGS),
        ...cloneDeep(EMPTY_PRODUCTION_DATES),
        ...cloneDeep(EMPTY_PROJECT_TRACK)
    };
};

export function safeUpdateObject(obj1, obj2) {
    for (const key of Object.keys(obj2)) {
        if (key in obj1) {
            const val1 = obj1[key];
            const val2 = obj2[key];

            if (typeof val1 === typeof val2) {
                if (
                    val1 !== null &&
                    val2 !== null &&
                    typeof val1 === 'object' &&
                    !Array.isArray(val1) &&
                    !Array.isArray(val2)
                ) {
                    obj1[key] = safeUpdateObject({ ...val1 }, val2);
                } else {
                    obj1[key] = val2 as any;
                }
            }
        }
    }
    return obj1;
}
